import { useState } from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { BasicSystemResponseModel, SystemConfigurationComponentsResponse } from 'src/Libs/shop/models'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { getImageUrlBySize } from 'src/Libs/shop/utils'
import useShopQntmSystemConfiguration from 'src/Libs/shop/hooks/use-qntm-system-configuration'
import { Component, ShippingDate } from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationModel'
import { getEm360Prices, getMainComponentPrices, getWarrantyExtensionPrices } from '../../../../utils/shop-system-configuration-price-calculation'

interface UseShopQntmSystemConfigurationCardReturn {
  basicData: BasicSystemResponseModel | null
  mainComponent: SystemConfigurationComponentsResponse | null
  totalPrice: number
  deliveryDate: ShippingDate | null
  discountedPrice: number
  basicSystemImageUrl: string | undefined
  selectedWarrantyExtensionId?: string | null
  selectedMainComponents: Component[] | null
}

/*
 *  Handles data and price summary logic for the normal system configuration card
 */
export const useShopQntmSystemConfigurationCard = (): UseShopQntmSystemConfigurationCardReturn => {
  const configuratorItems = useTypedSelector(state => state.qntmStorage.items)

  const deliveryDate = configuratorItems.length > 0 ? configuratorItems[0].desiredShippingDate : null
  const basicSystem = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const selectedWarrantyExtensionId = configuratorItems.length > 0 ? configuratorItems[0].configuration.warrantyExtension?.id : null
  const selectedMainComponents = configuratorItems.length > 0 ? configuratorItems[0].configuration.components : null
  const [basicData, setBasicData] = useState<BasicSystemResponseModel | null>(null)
  const [mainComponent, setMainComponent] = useState<SystemConfigurationComponentsResponse | null>(null)
  const [totalPrice, setTotalPrice] = useState<number>(0)
  const [discountedPrice, setDiscountedPrice] = useState<number>(0)
  const { getQntmSingleSystemConfiguration, getQntmSystemConfigurationComponents } = useShopQntmSystemConfiguration()

  useEffectShopAccess(() => {
    if (basicSystem != null) {
      getQntmSingleSystemConfiguration(basicSystem).then(result => setBasicData(result as BasicSystemResponseModel | null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicSystem, deliveryDate])

  useEffectShopAccess(() => {
    if (basicSystem) {
      getQntmSystemConfigurationComponents(basicSystem).then(result => setMainComponent(result))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMainComponents])

  useEffectShopAccess(() => {
    let normalSum = 0
    let discountedSum = 0

    const mainComponentPrices = getMainComponentPrices(mainComponent, selectedMainComponents)
    discountedSum += mainComponentPrices.price
    normalSum += mainComponentPrices.discountedPrice

    const em360Prices = getEm360Prices(mainComponent)
    discountedSum += em360Prices.price
    normalSum += em360Prices.discountedPrice

    const warrantyExtensionPrices = getWarrantyExtensionPrices(mainComponent, selectedWarrantyExtensionId)
    discountedSum += warrantyExtensionPrices.price
    normalSum += warrantyExtensionPrices.discountedPrice

    setTotalPrice(Number.parseFloat(discountedSum.toFixed(2)))
    setDiscountedPrice(Number.parseFloat(normalSum.toFixed(2)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainComponent, selectedMainComponents, selectedWarrantyExtensionId])

  const basicSystemImageUrl = getImageUrlBySize(basicData?.mainImage?.thumbnails, ShopImageThumnbailSizes.Small)

  return {
    basicData,
    deliveryDate,
    selectedMainComponents,
    selectedWarrantyExtensionId,
    mainComponent,
    totalPrice,
    discountedPrice,
    basicSystemImageUrl,
  }
}
